import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, SpaceBetween } from '@amzn/awsui-components-react';
import CONSTANTS from 'src/utils/constants';
import ErrorUtils from 'src/utils/ErrorUtils';
import ArrayUtils from 'src/utils/arrayUtils';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import { datasetReducer, ACTIONS } from 'src/services/tp-allocation/data-set/DatasetReducer';
import { initialState } from 'src/services/tp-allocation/data-set/DatasetState';
import { DatasetContext } from 'src/components/tp-allocation/shared/DatasetContainer';
import DatasetSplitGrid from 'src/components/tp-allocation/shared/DatasetSplitGrid';
import { TPELoadingSpinner } from 'src/components/shared/TPELoadingSpinner';
import { WorksheetMode } from 'src/models/tp-allocation/TPAllocationEnums';
import { TPAllocationState } from 'src/services/tp-allocation/TPAllocationState';
import { DerivedAllocationSheetBuilderContext } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderContext';
import { ACTIONS as DAS_ACTIONS } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderReducer';
import DasDataSetGrid, { formatFxRate } from './DasDataSetGrid';
import {
    toCreateDASDataSourceRequest, toDatasetRecords, toSavedDatasetRecord, toUpdateDASDataSourceRequest,
} from 'src/services/derived-allocation-sheet/dasDataSourceMapper';
import { DASDataSource } from 'src/models/derived-allocation-sheet/DASDataSource';
import DasDataSetEditorPanel from './DasDataSetEditorPanel';
import DasStepInfo from './DasStepInfo';

export const SAVE_RETURNED_NO_ROW_MESSAGE = 'The data source was saved, but the response did not include it. Refresh the page to see it.';

/**
 * Step 1 (Configure data set) of the derived allocation sheet builder, adapted from the TPA dataset container;
 * keep the two in sync. It reuses the TPA dataset reducer, COA selector, magic box and split grid. Rows load
 * and save through the derived allocation sheet service, and every write refreshes the sheet header.
 */
export default function DasDataSetSection(props: { onNextStep?: () => void }) {
    const { onNextStep } = props;
    const { dasState, dasDispatch, services, sheetId } = useContext(DerivedAllocationSheetBuilderContext);
    const { viewMode } = dasState;
    // The builder renders the steps only once a sheet is loaded.
    const sheetVersion = dasState.sheet!.sheetVersion;
    const [datasetState, datasetDispatch] = useReducerWithLogger(datasetReducer, initialState);
    const { datasetRecords, customCoaReference = new Map(), recordBeingEdited, recordBeingEditedOriginal } = datasetState;
    const showDataKeySelection = recordBeingEdited?.showDataKeySelection ?? false;
    const editable = viewMode === CONSTANTS.VIEW_MODE.EDITABLE;

    const [loadTime] = useState(() => new Date().toDateString());
    const [dataSourcesResult, dataSourcesLoading, loadError] =
        services.derivedAllocationSheetService.getDASDataSources('fetch', { sheetId, version: sheetVersion });
    // Set once the read rows are in the dataset state, so an empty sheet is seeded only after its rows arrive.
    const [loaded, setLoaded] = useState(false);
    const loading = dataSourcesLoading || (!loaded && !loadError);
    const [isSaving, setIsSaving] = useState(false);
    const [recordNeedsSaving, setRecordNeedsSaving] = useState(undefined as string | undefined);
    const selectCoaSaveRef = useRef<(() => boolean) | null>(null);

    const [dataSourceTypes, currencyCodes, periods, currencyConversionTypes] = services.dataSourcesService.getDataSourceLOVs(loadTime);
    const [coaTypes] = services.dataSourcesService.getCoaTypes(loadTime);
    const [sevenSegmentLOVMap] = services.tpAllocationService.getAllCoaSegments(`das-${sheetId}`);
    const sevenSegmentLOV = useMemo(() => sevenSegmentLOVMap ?? new Map<string, string[]>(), [sevenSegmentLOVMap]);

    useEffect(() => {
        if (dataSourcesResult == null) {
            return;
        }
        const { records, customCoaReferenceMap } = toDatasetRecords(dataSourcesResult.dataSources, sheetId);
        datasetDispatch(ACTIONS.SET_CUSTOM_COA_REFERENCE.withPayload(customCoaReferenceMap));
        datasetDispatch(ACTIONS.SET_RECORDS.withPayload(records));
        setLoaded(true);
    }, [dataSourcesResult]);

    // Seed one empty row for an empty editable sheet, once per load.
    const hasSeededRef = useRef(false);
    useEffect(() => { hasSeededRef.current = false; }, [sheetId, sheetVersion]);
    useEffect(() => {
        if (!loaded || loading || !editable || datasetRecords.length > 0 || hasSeededRef.current) {
            return;
        }
        hasSeededRef.current = true;
        datasetDispatch(ACTIONS.ADD_NEW_RECORD.withPayload({ parentEntityId: sheetId, worksheetMode: WorksheetMode.STANDALONE }));
    }, [loaded, loading, editable, datasetRecords.length]);

    // The later steps read the saved Step 1 rows from the builder state. The row being edited is published as
    // it was before the edit, so unsaved changes do not reach them.
    useEffect(() => {
        dasDispatch(DAS_ACTIONS.SET_DATA_SOURCES.withPayload(datasetRecords
            .filter((r) => !r.isNewRecord)
            .map((r) => (r === recordBeingEdited && recordBeingEditedOriginal != null ? recordBeingEditedOriginal : r))));
    }, [datasetRecords, recordBeingEdited, recordBeingEditedOriginal]);

    useEffect(() => {
        if (ArrayUtils.isNullOrEmpty(dataSourceTypes) || ArrayUtils.isNullOrEmpty(currencyCodes)
            || ArrayUtils.isNullOrEmpty(periods) || ArrayUtils.isNullOrEmpty(currencyConversionTypes)) {
            return;
        }
        datasetDispatch(ACTIONS.SET_TP_LOV.withPayload({
            dataSourceTypes, currencyCodes, periods, currencyConversionTypes,
        }));
    }, [dataSourceTypes, currencyCodes, periods, currencyConversionTypes]);

    // Saves the row being edited when the COA panel asks for it. A renamed row also refreshes Step 2.
    useEffect(() => {
        if (recordNeedsSaving == null || recordBeingEdited == null) {
            return;
        }
        const record = recordBeingEdited;
        const renamed = recordBeingEditedOriginal != null && recordBeingEditedOriginal.description !== record.description;
        let active = true;
        setIsSaving(true);
        const service = services.derivedAllocationSheetService;
        const save: Promise<{ dataSource: DASDataSource | null }> = record.isNewRecord
            ? service.createDASDataSource(toCreateDASDataSourceRequest(sheetId, record, customCoaReference))
            : service.updateDASDataSource(toUpdateDASDataSourceRequest(sheetId, record, customCoaReference));
        save
            .then(({ dataSource }) => {
                if (!active) return;
                // With no saved row to show, the editor stays open and the sheet is not refreshed.
                if (dataSource == null) {
                    services.messageService.showErrorBanner(SAVE_RETURNED_NO_ROW_MESSAGE);
                    return;
                }
                const saved = toSavedDatasetRecord(dataSource, sheetId);
                datasetDispatch(ACTIONS.SAVE_RECORD.withPayload({ recordID: record.datasourceId, updatedRecord: saved }));
                if (renamed) {
                    dasDispatch(DAS_ACTIONS.REFRESH_TOTALS);
                }
                dasDispatch(DAS_ACTIONS.REFRESH_SHEET);
            })
            .catch((ex) => {
                if (active) services.messageService.showErrorBanner(ErrorUtils.getMessage(ex));
            })
            .finally(() => {
                if (active) setIsSaving(false);
            });
        return () => {
            active = false;
        };
    }, [recordNeedsSaving]);

    const addNewRecord = () => {
        datasetDispatch(ACTIONS.ADD_NEW_RECORD.withPayload({ parentEntityId: sheetId, worksheetMode: WorksheetMode.STANDALONE }));
    };

    const atLeastOneSaved = datasetRecords.some((x) => !x.isNewRecord);
    const saving = isSaving || recordNeedsSaving != null;
    const hasUnsavedWork = saving || datasetRecords.some((x) => x.isNewRecord) || !!recordBeingEdited?.isEditing;

    // The builder keeps another version from opening while Step 1 has work that is not saved.
    useEffect(() => {
        dasDispatch(DAS_ACTIONS.SET_STEP_UNSAVED.withPayload({ step: 1, unsaved: hasUnsavedWork, saving }));
    }, [hasUnsavedWork, saving]);
    useEffect(() => () => dasDispatch(DAS_ACTIONS.SET_STEP_UNSAVED.withPayload({ step: 1, unsaved: false })), []);
    // The split grid shows the rows below the one being edited; format their FX rates as the Step 1 grid does.
    const formatSplitGridFxRate = useCallback((fxRate?: string) =>
        formatFxRate(fxRate, (v) => services.formattingService.formatString(v, true)), [services]);

    // The TPA split grid reads only viewMode and worksheetMode from the TPA state. It offers its row menus only
    // when EDITABLE, so they are hidden while a save is in flight.
    const splitGridViewMode = isSaving ? CONSTANTS.VIEW_MODE.READ_ONLY : viewMode;
    const bridgeValue = useMemo(() => ({
        datasetState,
        datasetDispatch,
        services,
        tpAllocationState: { viewMode: splitGridViewMode, worksheetMode: WorksheetMode.STANDALONE } as unknown as TPAllocationState,
    }), [datasetState, datasetDispatch, splitGridViewMode]);

    return (
        <DatasetContext.Provider value={bridgeValue}>
            <div className="wizardBoxContentContainer" data-class="dasStep1">
                <SpaceBetween direction="vertical" size="s">
                    <DasStepInfo>
                        Per-entity driver balances from General Ledger or Secondary Ledger. The description is the name
                        Steps 2 and 3 use to refer to this row.
                    </DasStepInfo>
                    {!!loadError && (
                        <Alert type="error" header="Unable to load data sources">{loadError}</Alert>
                    )}
                </SpaceBetween>
                <div className="datasourceHeader">
                    <TPELoadingSpinner loading={loading} loadingText="Loading data sources">
                        <DasDataSetGrid saving={isSaving} />
                    </TPELoadingSpinner>
                </div>
                {showDataKeySelection && editable &&
                    <DasDataSetEditorPanel coaTypes={coaTypes} sevenSegmentLOV={sevenSegmentLOV} isSaving={isSaving}
                                           onRecordNeedsSaving={setRecordNeedsSaving} saveRecordRef={selectCoaSaveRef} />
                }
                {recordBeingEdited != undefined && datasetRecords.indexOf(recordBeingEdited) != datasetRecords.length - 1 &&
                    <div><DatasetSplitGrid allowBalancePull={false} formatFxRate={formatSplitGridFxRate} /></div>
                }
                {editable &&
                    <div className="actionButtonsContainer">
                        <Box float="left">
                            <Button data-class="subPrimaryButton" onClick={addNewRecord}
                                    disabled={recordBeingEdited?.isEditing || loading}>Add data source</Button>
                        </Box>
                        <Box float="right">
                            <Button data-class="nextButton" variant="primary" disabled={!atLeastOneSaved || loading}
                                    onClick={() => onNextStep?.()}>Next Step</Button>
                        </Box>
                    </div>
                }
            </div>
        </DatasetContext.Provider>
    );
}
